/**
 * API Keys Page
 *
 * Super admin only view for managing API keys across all clinics.
 * Displays clinic_auth_apis and clinic_api_tokens collections.
 */

import { useState, useEffect, useCallback } from 'react'
import { apiKeysAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
  Button, Badge
} from '../components/ui'
import { RefreshCw, Loader2, AlertCircle, Key, Shield, Clock, CheckCircle, XCircle } from 'lucide-react'

// Format date
function formatDate(date) {
  if (!date) return '-'
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Status badge component
function StatusBadge({ isActive }) {
  if (isActive === undefined || isActive === null) {
    return <Badge variant="outline">Unknown</Badge>
  }
  return isActive ? (
    <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
      <CheckCircle className="h-3 w-3 mr-1" />
      Active
    </Badge>
  ) : (
    <Badge variant="destructive">
      <XCircle className="h-3 w-3 mr-1" />
      Inactive
    </Badge>
  )
}

// Auth APIs Table - structure: { path, tokens: [], roles: [], users: [] }
function AuthApisTable({ apis }) {
  if (!apis || apis.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No auth APIs found across any clinic.
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left py-3 px-3 font-medium text-muted-foreground">Clinic</th>
            <th className="text-left py-3 px-3 font-medium text-muted-foreground">Path</th>
            <th className="text-left py-3 px-3 font-medium text-muted-foreground">Roles</th>
            <th className="text-center py-3 px-3 font-medium text-muted-foreground">Tokens</th>
            <th className="text-center py-3 px-3 font-medium text-muted-foreground">Users</th>
          </tr>
        </thead>
        <tbody>
          {apis.map((api) => (
            <tr key={api._id} className="border-b border-border/50 hover:bg-muted/50">
              <td className="py-3 px-3">
                <div className="font-medium">{api.clinicName}</div>
                <div className="text-xs text-muted-foreground">{api.clinicId}</div>
              </td>
              <td className="py-3 px-3">
                <code className="px-2 py-0.5 bg-muted rounded text-xs font-mono">
                  {api.path || '-'}
                </code>
              </td>
              <td className="py-3 px-3">
                <div className="flex flex-wrap gap-1">
                  {api.roles?.length > 0 ? (
                    api.roles.map((role, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">
                        {role}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </div>
              </td>
              <td className="py-3 px-3 text-center">
                <Badge variant="outline">{api.tokens?.length || 0}</Badge>
              </td>
              <td className="py-3 px-3 text-center">
                <Badge variant="outline">{api.users?.length || 0}</Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// API Tokens Table - structure: { name, token (hashed), created_at, is_active }
function ApiTokensTable({ tokens }) {
  if (!tokens || tokens.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No API tokens found across any clinic.
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left py-3 px-3 font-medium text-muted-foreground">Clinic</th>
            <th className="text-left py-3 px-3 font-medium text-muted-foreground">Name</th>
            <th className="text-center py-3 px-3 font-medium text-muted-foreground">Status</th>
            <th className="text-center py-3 px-3 font-medium text-muted-foreground">Token</th>
            <th className="text-left py-3 px-3 font-medium text-muted-foreground">Created</th>
          </tr>
        </thead>
        <tbody>
          {tokens.map((token) => (
            <tr key={token._id} className="border-b border-border/50 hover:bg-muted/50">
              <td className="py-3 px-3">
                <div className="font-medium">{token.clinicName}</div>
                <div className="text-xs text-muted-foreground">{token.clinicId}</div>
              </td>
              <td className="py-3 px-3 font-medium">{token.name || '-'}</td>
              <td className="py-3 px-3 text-center">
                <StatusBadge isActive={token.is_active} />
              </td>
              <td className="py-3 px-3 text-center">
                <Badge variant={token.hasToken ? 'default' : 'outline'} className="text-xs">
                  {token.hasToken ? 'Configured' : 'Not set'}
                </Badge>
              </td>
              <td className="py-3 px-3 text-muted-foreground">
                {formatDate(token.created_at)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// Summary cards
function SummaryCards({ summary }) {
  if (!summary) return null

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <Shield className="h-4 w-4" />
            <span className="text-xs">Auth APIs</span>
          </div>
          <div className="text-2xl font-bold">{summary.totalAuthApis}</div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <Key className="h-4 w-4" />
            <span className="text-xs">API Tokens</span>
          </div>
          <div className="text-2xl font-bold">{summary.totalApiTokens}</div>
        </CardContent>
      </Card>
      <Card className="col-span-2">
        <CardContent className="pt-6">
          <div className="text-xs text-muted-foreground mb-2">By Clinic</div>
          <div className="flex flex-wrap gap-3">
            {Object.entries(summary.byClinic || {}).map(([clinicId, data]) => (
              <div key={clinicId} className="text-sm">
                <span className="font-medium">{data.name}:</span>
                <span className="ml-1 text-muted-foreground">
                  {data.authApis} APIs, {data.apiTokens} tokens
                </span>
                {data.error && (
                  <span className="ml-1 text-destructive text-xs">({data.error})</span>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default function ApiKeysPage() {
  const { user } = useAuthStore()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState('auth') // 'auth' or 'tokens'

  // Fetch API keys
  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await apiKeysAPI.getAll()
      if (response.data?.data) {
        setData(response.data.data)
      }
    } catch (err) {
      console.error('Failed to fetch API keys:', err)
      setError(err.response?.data?.error?.message || 'Failed to load API keys')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (user?.role === 'super_admin') {
      fetchData()
    }
  }, [fetchData, user?.role])

  // Check if user is super admin
  if (user?.role !== 'super_admin') {
    return (
      <div className="p-6">
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Access Denied
            </CardTitle>
            <CardDescription>
              This page is only accessible to super administrators.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Key className="h-6 w-6" />
            API Keys
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage API authentication keys and tokens across all clinics
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchData}
          disabled={loading}
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          <span className="ml-2">Refresh</span>
        </Button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-destructive">Error loading data</h3>
            <p className="text-sm text-destructive/80 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && !data && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Content */}
      {data && (
        <>
          {/* Summary Cards */}
          <SummaryCards summary={data.summary} />

          {/* Tabs */}
          <div className="flex gap-2 border-b">
            <button
              onClick={() => setActiveTab('auth')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'auth'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Shield className="h-4 w-4 inline mr-2" />
              Auth APIs ({data.authApis?.length || 0})
            </button>
            <button
              onClick={() => setActiveTab('tokens')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'tokens'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Key className="h-4 w-4 inline mr-2" />
              API Tokens ({data.apiTokens?.length || 0})
            </button>
          </div>

          {/* Auth APIs Tab */}
          {activeTab === 'auth' && (
            <Card>
              <CardContent className="pt-6">
                <AuthApisTable apis={data.authApis} />
              </CardContent>
            </Card>
          )}

          {/* API Tokens Tab */}
          {activeTab === 'tokens' && (
            <Card>
              <CardContent className="pt-6">
                <ApiTokensTable tokens={data.apiTokens} />
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Footer note */}
      <div className="text-xs text-muted-foreground text-center">
        Sensitive values are masked for security. API keys are read-only in this view.
      </div>
    </div>
  )
}
